import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { NumberFormatter } from "app/common/utils/number-formatter";

import { Arrow } from "../../../../../components/Arrow/Arrow";

import { IndicatorBadgeWrapper } from "./styled";

export interface IIndicatorBadgeProps {
    /** The numeric value to display as a percentage */
    value: number | null | undefined;
    /**
     * Determines color interpretation based on value direction
     * - "normal": positive values show green, negative values show red
     * - "inverted": positive values show red, negative values show green
     * @default "normal"
     */
    behavior?: "normal" | "inverted";
    label?: string;
}

/**
 * Badge component that displays a percentage value with directional arrow and color coding
 * @param value - The numeric value to display as a percentage
 * @param label - Optional label to display after the percentage value
 * @param behavior - Color interpretation mode: "normal" (positive=green) or "inverted" (positive=red)
 */
export const IndicatorBadge = ({ value, label, behavior = "normal" }: IIndicatorBadgeProps) => {
    if (value === null || value === undefined) {
        return (
            <IndicatorBadgeWrapper $bgColor={colorsPalettes.carbon["100"]}>
                <Text
                    fontVariant="primaryItem"
                    color={colorsPalettes.carbon["400"]}
                >
                    {`—${label ? ` ${label}` : ""}`}
                </Text>
            </IndicatorBadgeWrapper>
        );
    }

    const isNormal = behavior === "normal";
    const isPositiveChange = value > 0;
    const shouldShowGreen = isNormal ? isPositiveChange : !isPositiveChange;
    const bgColor = shouldShowGreen ? colorsPalettes.green["100"] : colorsPalettes.red["100"];
    const fontColor: StrictColor = shouldShowGreen ? colorsPalettes.green["400_P"] : colorsPalettes.red["s100"];

    return (
        <IndicatorBadgeWrapper $bgColor={bgColor}>
            <Arrow
                color={fontColor}
                size={14}
                direction={value > 0 ? "up" : "down"}
            />
            <Text
                fontVariant="primaryItem"
                color={fontColor}
            >
                {`${NumberFormatter.toPercent(Math.abs(value) / 100, 0, "—")}${label ? ` ${label}` : ""}`}
            </Text>
        </IndicatorBadgeWrapper>
    );
};
